import { Edit, Eye, Plus, Search, Tags, Trash2 } from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'
import { toast } from 'sonner'

import { useDebounce } from '../../hooks/useDebounce'
import { CatalogSelect } from '../components/ui/CatalogSelect'
import { Badge } from '../components/ui/badge'
import { Button } from '../components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog'
import { GlobalPaginator } from '../components/ui/global-paginator'
import { Input } from '../components/ui/input'
import { Textarea } from '../components/ui/textarea'
import {
  ProductUseCase,
  RuleEffect,
  UseCasePayload,
  UseCaseRule,
  useCaseService,
} from '../services/use-case.service'
import { useSessionStore } from '../store/auth/auth.store'

interface PreviewProduct {
  id: number
  name: string
  sku: string
  use_case_priority: number
}

interface PreviewPagination {
  total: number
  perPage: number
  from: number
  to: number
}

const emptyForm: UseCasePayload = {
  name: '',
  description: '',
  is_active: true,
  sort_order: 0,
  categories: [],
  departments: [],
  product_types: [],
  products: [],
}

interface RuleEditorProps {
  title: string
  catalog: string
  rules: UseCaseRule[]
  defaultPriority: number
  onChange: (rules: UseCaseRule[]) => void
  allowEffect?: boolean
}

function RuleEditor({ title, catalog, rules, defaultPriority, onChange, allowEffect = false }: RuleEditorProps) {
  const addRule = (item: { id: number; name?: string; title?: string; sku?: string }) => {
    if (rules.some((rule) => Number(rule.id) === Number(item.id))) return
    onChange([
      ...rules,
      {
        id: Number(item.id),
        name: item.name || item.title || item.sku || `ID ${item.id}`,
        sku: item.sku,
        priority: defaultPriority,
        effect: allowEffect ? 'include' : undefined,
      },
    ])
  }

  return (
    <section className="space-y-2 rounded-lg border border-slate-200 p-3">
      <div className="flex items-center justify-between gap-3">
        <h3 className="text-sm font-semibold text-slate-800">{title}</h3>
        <span className="text-xs text-slate-500">{rules.length} reglas</span>
      </div>
      <CatalogSelect
        catalog={catalog}
        returnObject
        onChange={addRule}
        placeholder={`Agregar ${title.toLowerCase()}...`}
      />
      <div className="max-h-40 space-y-2 overflow-y-auto">
        {rules.map((rule) => (
          <div key={rule.id} className="flex items-center gap-2 rounded bg-slate-50 p-2 text-sm">
            <div className="min-w-0 flex-1">
              <p className="truncate font-medium">{rule.name}</p>
              {rule.sku && <p className="truncate text-xs text-slate-500">SKU {rule.sku}</p>}
            </div>
            {allowEffect && (
              <select
                className="h-8 rounded border border-slate-200 bg-white px-2"
                value={rule.effect}
                onChange={(event) => onChange(rules.map((item) => item.id === rule.id
                  ? { ...item, effect: event.target.value as RuleEffect }
                  : item))}
              >
                <option value="include">Incluir</option>
                <option value="exclude">Excluir</option>
              </select>
            )}
            <label className="space-y-0.5 text-[11px] font-medium text-slate-500">
              Prioridad
              <Input
                aria-label={`Prioridad de ${rule.name}`}
                className="h-8 w-20 text-sm text-slate-900"
                type="number"
                min={0}
                max={100}
                value={rule.priority}
                onChange={(event) => onChange(rules.map((item) => item.id === rule.id
                  ? { ...item, priority: Math.max(0, Math.min(100, Number(event.target.value))) }
                  : item))}
              />
            </label>
            <button
              type="button"
              className="rounded p-1 text-red-600 hover:bg-red-50"
              onClick={() => onChange(rules.filter((item) => item.id !== rule.id))}
              aria-label={`Quitar ${rule.name}`}
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        ))}
      </div>
      {allowEffect && (
        <p className="text-xs text-slate-500">
          Las exclusiones siempre eliminan el producto, independientemente de su prioridad.
        </p>
      )}
    </section>
  )
}

export function UseCases() {
  const [items, setItems] = useState<ProductUseCase[]>([])
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const [pagination, setPagination] = useState({ total: 0, perPage: 15, from: 0, to: 0 })
  const [loading, setLoading] = useState(true)
  const [open, setOpen] = useState(false)
  const [saving, setSaving] = useState(false)
  const [editingId, setEditingId] = useState<number | null>(null)
  const [form, setForm] = useState<UseCasePayload>(emptyForm)
  const [categoryRules, setCategoryRules] = useState<UseCaseRule[]>([])
  const [departmentRules, setDepartmentRules] = useState<UseCaseRule[]>([])
  const [productTypeRules, setProductTypeRules] = useState<UseCaseRule[]>([])
  const [productRules, setProductRules] = useState<UseCaseRule[]>([])
  const [preview, setPreview] = useState<{
    useCase: ProductUseCase
    products: PreviewProduct[]
    pagination: PreviewPagination
  } | null>(null)
  const [previewSearch, setPreviewSearch] = useState('')
  const [previewPage, setPreviewPage] = useState(1)
  const [previewLoading, setPreviewLoading] = useState(false)
  const debouncedPreviewSearch = useDebounce(previewSearch, 300)
  const canCreateUseCase = useSessionStore((s) => s.canAction('inventory.use-case.create'))
  const canEditUseCase = useSessionStore((s) => s.canAction('inventory.use-case.edit'))
  const canDeleteUseCase = useSessionStore((s) => s.canAction('inventory.use-case.delete'))
  const canChangeUseCaseStatus = useSessionStore((s) => s.canAction('inventory.use-case.status'))

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const result = await useCaseService.list({ page, per_page: 15, search })
      setItems(result.data)
      setPagination({ total: result.total, perPage: result.per_page, from: result.from || 0, to: result.to || 0 })
    } finally {
      setLoading(false)
    }
  }, [page, search])

  useEffect(() => { void load() }, [load])

  const previewUseCaseId = preview?.useCase.id

  useEffect(() => {
    if (!previewUseCaseId) return

    let active = true
    setPreviewLoading(true)

    useCaseService.preview(previewUseCaseId, previewPage, debouncedPreviewSearch)
      .then((result) => {
        if (!active) return
        setPreview((current) => current ? {
          ...current,
          products: result.data,
          pagination: {
            total: result.total,
            perPage: result.per_page,
            from: result.from || 0,
            to: result.to || 0,
          },
        } : null)
      })
      .finally(() => {
        if (active) setPreviewLoading(false)
      })

    return () => { active = false }
  }, [previewUseCaseId, previewPage, debouncedPreviewSearch])

  const openCreate = () => {
    if (!canCreateUseCase) return
    setEditingId(null)
    setForm(emptyForm)
    setCategoryRules([])
    setDepartmentRules([])
    setProductTypeRules([])
    setProductRules([])
    setOpen(true)
  }

  const openEdit = async (id: number) => {
    if (!canEditUseCase) return
    const useCase = await useCaseService.get(id)
    setEditingId(id)
    setForm({
      name: useCase.name,
      description: useCase.description || '',
      is_active: useCase.is_active,
      sort_order: useCase.sort_order,
      categories: [], departments: [], product_types: [], products: [],
    })
    setCategoryRules((useCase.categories || []).map((rule) => ({ ...rule, priority: rule.pivot.priority })))
    setDepartmentRules((useCase.departments || []).map((rule) => ({ ...rule, priority: rule.pivot.priority })))
    setProductTypeRules((useCase.product_types || []).map((rule) => ({ ...rule, priority: rule.pivot.priority })))
    setProductRules((useCase.product_overrides || []).map((rule) => ({
      ...rule,
      priority: rule.pivot.priority,
      effect: rule.pivot.effect,
    })))
    setOpen(true)
  }

  const save = async () => {
    if (editingId && !canEditUseCase) return
    if (!editingId && !canCreateUseCase) return

    if (!form.name.trim()) {
      toast.error('El nombre es obligatorio.')
      return
    }
    setSaving(true)
    const payload: UseCasePayload = {
      ...form,
      categories: categoryRules.map(({ id, priority }) => ({ id, priority })),
      departments: departmentRules.map(({ id, priority }) => ({ id, priority })),
      product_types: productTypeRules.map(({ id, priority }) => ({ id, priority })),
      products: productRules.map(({ id, priority, effect }) => ({ id, priority, effect: effect || 'include' })),
    }
    try {
      if (editingId) {
        await useCaseService.update(editingId, payload)
      } else {
        await useCaseService.create(payload)
      }
      toast.success(`Caso de uso ${editingId ? 'actualizado' : 'creado'} exitosamente.`)
      setOpen(false)
      await load()
    } catch {
      toast.error('No se pudo guardar el caso de uso.')
    } finally {
      setSaving(false)
    }
  }

  const showPreview = async (useCase: ProductUseCase) => {
    setPreviewSearch('')
    setPreviewPage(1)
    const detail = await useCaseService.get(useCase.id)
    setPreview({
      useCase: detail,
      products: [],
      pagination: { total: 0, perPage: 20, from: 0, to: 0 },
    })
  }

  const toggleStatus = async (useCase: ProductUseCase) => {
    if (!canChangeUseCaseStatus) return
    await useCaseService.updateStatus(useCase.id, !useCase.is_active)
    toast.success('Estado actualizado.')
    await load()
  }

  const remove = async (useCase: ProductUseCase) => {
    if (!canDeleteUseCase) return
    if (!window.confirm(`¿Eliminar el caso de uso “${useCase.name}”?`)) return
    await useCaseService.remove(useCase.id)
    toast.success('Caso de uso eliminado.')
    await load()
  }

  return (
    <div className="space-y-6 p-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Casos de uso</h1>
          <p className="mt-1 text-sm text-slate-600">Agrupa productos por la necesidad que resuelven.</p>
        </div>
        <div className="flex gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input value={search} onChange={(event) => { setSearch(event.target.value); setPage(1) }} placeholder="Buscar..." className="pl-9" />
          </div>
          {canCreateUseCase && <Button onClick={openCreate}><Plus className="mr-2 h-4 w-4" />Nuevo caso</Button>}
        </div>
      </div>

      <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
        <table className="w-full">
          <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500">
            <tr><th className="px-5 py-3">Caso</th><th className="px-5 py-3">Reglas</th><th className="px-5 py-3">Estado</th><th className="px-5 py-3 text-right">Acciones</th></tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading ? <tr><td className="p-8 text-center text-slate-500" colSpan={4}>Cargando...</td></tr> : items.map((item) => (
              <tr key={item.id}>
                <td className="px-5 py-4"><p className="font-medium">{item.name}</p><p className="text-sm text-slate-500">{item.description}</p></td>
                <td className="px-5 py-4 text-sm text-slate-600">
                  {(item.categories_count || 0) + (item.departments_count || 0) + (item.product_types_count || 0)} catálogos · {item.product_overrides_count || 0} excepciones
                </td>
                <td className="px-5 py-4">
                  <button hidden={!canChangeUseCaseStatus} onClick={() => void toggleStatus(item)}><Badge className={item.is_active ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-600'}>{item.is_active ? 'Activo' : 'Inactivo'}</Badge></button>
                  {!canChangeUseCaseStatus && <Badge className={item.is_active ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-600'}>{item.is_active ? 'Activo' : 'Inactivo'}</Badge>}
                </td>
                <td className="px-5 py-4"><div className="flex justify-end gap-1">
                  <Button variant="ghost" size="icon" onClick={() => void showPreview(item)} title="Vista previa"><Eye className="h-4 w-4" /></Button>
                  {canEditUseCase && <Button variant="ghost" size="icon" onClick={() => void openEdit(item.id)} title="Editar"><Edit className="h-4 w-4" /></Button>}
                  {canDeleteUseCase && <Button variant="ghost" size="icon" onClick={() => void remove(item)} title="Eliminar"><Trash2 className="h-4 w-4 text-red-600" /></Button>}
                </div></td>
              </tr>
            ))}
            {!loading && items.length === 0 && <tr><td colSpan={4} className="p-12 text-center text-slate-500"><Tags className="mx-auto mb-2 h-8 w-8" />No hay casos de uso.</td></tr>}
          </tbody>
        </table>
        {items.length > 0 && <GlobalPaginator totalItems={pagination.total} itemsPerPage={pagination.perPage} currentPage={page} from={pagination.from} to={pagination.to} onPageChange={setPage} />}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-4xl">
          <DialogHeader><DialogTitle>{editingId ? 'Editar caso de uso' : 'Nuevo caso de uso'}</DialogTitle><DialogDescription>Una coincidencia con cualquier regla incluye el producto; una exclusión directa siempre gana.</DialogDescription></DialogHeader>
          <div className="grid gap-4 sm:grid-cols-2">
            <label className="space-y-1 text-sm">Nombre<Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></label>
            <label className="space-y-1 text-sm">
              Orden de aparición
              <Input type="number" min={0} value={form.sort_order} onChange={(e) => setForm({ ...form, sort_order: Number(e.target.value) })} />
              <span className="block text-xs text-slate-500">Los números menores aparecen primero.</span>
            </label>
            <label className="space-y-1 text-sm sm:col-span-2">Descripción<Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></label>
            <label hidden={!canChangeUseCaseStatus} className="flex items-center gap-2 text-sm sm:col-span-2"><input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} />Disponible</label>
            <p className="text-xs text-slate-500 sm:col-span-2">
              Las reglas con mayor prioridad aparecen primero en los resultados. Si un producto coincide con varias reglas, se utiliza la prioridad más alta.
            </p>
            <RuleEditor title="Categorías" catalog="categories" rules={categoryRules} defaultPriority={70} onChange={setCategoryRules} />
            <RuleEditor title="Departamentos" catalog="departments" rules={departmentRules} defaultPriority={50} onChange={setDepartmentRules} />
            <RuleEditor title="Tipos de producto" catalog="product_types" rules={productTypeRules} defaultPriority={30} onChange={setProductTypeRules} />
            <RuleEditor title="Productos específicos" catalog="products" rules={productRules} defaultPriority={100} onChange={setProductRules} allowEffect />
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button><Button disabled={saving} onClick={() => void save()}>{saving ? 'Guardando...' : 'Guardar'}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={preview !== null} onOpenChange={(value) => {
        if (!value) {
          setPreview(null)
          setPreviewSearch('')
          setPreviewPage(1)
        }
      }}>
        <DialogContent className="max-h-[80vh] overflow-y-auto sm:max-w-3xl">
          <DialogHeader><DialogTitle>Vista previa: {preview?.useCase.name}</DialogTitle><DialogDescription>Resultados ordenados por prioridad.</DialogDescription></DialogHeader>
          {preview && (
            <div className="space-y-3 rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm">
              {(preview.useCase.departments?.length || 0) > 0 && (
                <div>
                  <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">Departamentos</p>
                  <div className="flex flex-wrap gap-1.5">
                    {preview.useCase.departments?.map((department) => (
                      <Badge key={department.id} className="bg-blue-100 text-blue-700">{department.name}</Badge>
                    ))}
                  </div>
                </div>
              )}
              {(preview.useCase.categories?.length || 0) > 0 && (
                <div>
                  <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">Categorías</p>
                  <div className="flex flex-wrap gap-1.5">
                    {preview.useCase.categories?.map((category) => (
                      <Badge key={category.id} className="bg-emerald-100 text-emerald-700">{category.name}</Badge>
                    ))}
                  </div>
                </div>
              )}
              {(preview.useCase.product_types?.length || 0) > 0 && (
                <div>
                  <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">Tipos de producto</p>
                  <div className="flex flex-wrap gap-1.5">
                    {preview.useCase.product_types?.map((productType) => (
                      <Badge key={productType.id} className="bg-violet-100 text-violet-700">{productType.name}</Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
            <label className="relative block flex-1 text-sm">
              <span className="mb-1 block font-medium text-slate-700">Buscar en este caso de uso</span>
              <Search className="absolute bottom-2.5 left-3 h-4 w-4 text-slate-400" />
              <Input
                type="search"
                value={previewSearch}
                onChange={(event) => {
                  setPreviewSearch(event.target.value)
                  setPreviewPage(1)
                }}
                placeholder="Nombre, código de barras..."
                className="pl-9"
              />
            </label>
            <p className="pb-2 text-sm text-slate-600">
              {previewLoading && preview?.pagination.total === 0
                ? 'Consultando productos...'
                : `${preview?.pagination.total || 0} productos coincidentes`}
            </p>
          </div>
          <div className="divide-y rounded border">
            {previewLoading ? (
              <p className="p-8 text-center text-slate-500">Cargando productos...</p>
            ) : (
              <>
                {preview?.products.map((product) => <div key={product.id} className="flex items-center justify-between p-3"><div><p className="font-medium">{product.name}</p><p className="text-xs text-slate-500">{product.sku}</p></div><Badge>{product.use_case_priority}</Badge></div>)}
                {preview?.products.length === 0 && <p className="p-8 text-center text-slate-500">No hay productos coincidentes.</p>}
              </>
            )}
          </div>
          {preview && preview.pagination.total > 0 && (
            <GlobalPaginator
              totalItems={preview.pagination.total}
              itemsPerPage={preview.pagination.perPage}
              currentPage={previewPage}
              from={preview.pagination.from}
              to={preview.pagination.to}
              onPageChange={setPreviewPage}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  )
}
